import { Box, Flex, Text } from '@chakra-ui/react';
import { ChangeEvent, useState } from 'react';
import { ProgressBar } from 'ms-react-progress-bar';
import { AlignmentType, BackgroundType, ContainerStyle, IProgressOptions, LabelPosition } from 'ms-react-progress-bar/dist/models/models';
import { CustomNumberInput } from './CustomNumberInput';
import { CustomInput } from './CustomInput';
import { CustomSwitch } from './CustomSwitch';
import { CustomCheckBox } from './CustomCheckBox';
import { ColorBox } from './ColorBox';
import { CustomOptions } from './CustomOptions';

export const CustomizeProgressBar = () => {
	const [value, setValue] = useState(20);
	const [options, setOptions]: [IProgressOptions, any] = useState({
		type: 'regular',
		maxValue: 100,
		height: '22px',
		borderRadius: '20px',
		labelSize: '12px',
		stripeAnimationDuration: '10s',
		customLabel: '',
		stripeAnimation: false,
		labelVisibility: true,
		barColor: '#2c43ac',
		containerColor: '#dddddd',
		labelColor: '#ffffff',
		labelAlignment: 'left',
		containerStyle: 'bg',
		showMaxValue: false,
		labelPosition: "center",
	});
	const progressBarTypes: BackgroundType[] = ['regular', 'striped'];
	const labelAlignments: AlignmentType[] = ['left', 'center', 'right'];
	const labelPositions: LabelPosition[] = ['top', 'center', 'bottom'];
	const containerStyles: ContainerStyle[] = ['bg', 'border', 'none'];

	const handleChange = (value: string) => setValue(Number(value));
	const handleMaxValueChange = (value: string) =>
		setOptions((options: IProgressOptions) => ({ ...options, maxValue: Number(value) }));
	const handleHeightChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({ ...options, height: e.target.value }));
	const handleBorderRadiusChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({ ...options, borderRadius: e.target.value }));
	const handleLabelSizeChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({ ...options, labelSize: e.target.value }));
	const handleStripeAnimationDurationChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({ ...options, stripeAnimationDuration: e.target.value }));
	const handleCustomLabelChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({ ...options, customLabel: e.target.value }));
	const handleTypeChange = (type: string) => setOptions((options: IProgressOptions) => ({ ...options, type }));
	const handleLabelAlignmentChange = (labelAlignment: string) =>
		setOptions((options: IProgressOptions) => ({ ...options, labelAlignment }));
	const handleLabelPositionChange = (labelPosition: string) =>
		setOptions((options: IProgressOptions) => ({ ...options, labelPosition }));
	const handleContainerStyleChange = (containerStyle: string) =>
		setOptions((options: IProgressOptions) => ({ ...options, containerStyle }));
	const handleStripeAnimationChange = () =>
		setOptions((options: IProgressOptions) => ({
			...options,
			stripeAnimation: !options.stripeAnimation
		}));
	const handleLabelVisibilityChange = () =>
		setOptions((options: IProgressOptions) => ({
			...options,
			labelVisibility: !options.labelVisibility
		}));
	const handleShowMaxValueChange = () =>
		setOptions((options: IProgressOptions) => ({
			...options,
			showMaxValue: !options.showMaxValue
		}));
	const handleContainerColorChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({
			...options,
			containerColor: e.target.value
		}));
	const handleLabelColorChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({
			...options,
			labelColor: e.target.value
		}));
	const handleBarColorChange = (e: ChangeEvent<HTMLInputElement>) =>
		setOptions((options: IProgressOptions) => ({
			...options,
			barColor: e.target.value
		}));

	return (
		<Box p="10px">
			<ProgressBar
				value={value}
				options={{
					...Object.entries(options).reduce<{
						[key: string]: string | number | boolean;
					}>((acc, [key, value]) => {
						if (!!value.toString()) {
							acc[key] = value;
						}
						return acc;
					}, {})
				}}
			/>
			<Flex flexWrap="wrap">
				<Box display="inline-block">
					<Text fontSize="md" fontWeight="bold" mx="10px" my="5px">
						Genera Options
					</Text>
					<Flex flexWrap="wrap" alignItems="center" mb="20px">
						<CustomNumberInput label="Value" value={value} handleChange={handleChange} />
						<CustomNumberInput
							label="Max Value"
							value={options.maxValue!}
							handleChange={handleMaxValueChange}
						/>
						<CustomCheckBox
							label="Show Max Value"
							value={options.showMaxValue!}
							handleChange={handleShowMaxValueChange}
						/>
						<CustomInput value={options.height!} label="Height" handleChange={handleHeightChange} />
						<CustomInput
							value={options.borderRadius!}
							label="Border Radius"
							handleChange={handleBorderRadiusChange}
						/>
					</Flex>
					<Text fontSize="md" fontWeight="bold" mx="10px" my="5px">
						Progress Bar Options
					</Text>
					<Flex flexWrap="wrap" alignItems="center" mb="20px">
						<CustomSwitch
							values={progressBarTypes}
							handleChange={handleTypeChange}
							label="Type: "
							defaultValue="regular"
						/>
						<ColorBox label="Bar Color" value={options.barColor!} handleChange={handleBarColorChange} />
						<CustomCheckBox
							label="Stripe Animation"
							value={options.stripeAnimation!}
							handleChange={handleStripeAnimationChange}
						/>
						<CustomInput
							value={options.stripeAnimationDuration!}
							label="Striped Animation Duration"
							handleChange={handleStripeAnimationDurationChange}
							width="150px"
						/>
					</Flex>

					<Text fontSize="md" fontWeight="bold" mx="10px" my="5px">
						Container Options
					</Text>
					<Flex flexWrap="wrap" alignItems="center" mb="20px">
						<CustomSwitch
							values={containerStyles}
							handleChange={handleContainerStyleChange}
							label="Container Style: "
							defaultValue="bg"
						/>
						<ColorBox
							label="Container Color"
							value={options.containerColor!}
							handleChange={handleContainerColorChange}
						/>
					</Flex>

					<Text fontSize="md" fontWeight="bold" mx="10px" my="5px">
						Label Options
					</Text>
					<Flex flexWrap="wrap" alignItems="center" mb="20px">
						<CustomSwitch
							values={labelAlignments}
							handleChange={handleLabelAlignmentChange}
							label="Label Alignment: "
							defaultValue="left"
						/>
						<CustomSwitch
							values={labelPositions}
							handleChange={handleLabelPositionChange}
							label="Label Position: "
							defaultValue="center"
						/>
						<CustomInput
							value={options.labelSize!}
							label="Label Size"
							handleChange={handleLabelSizeChange}
						/>
						<CustomInput
							value={options.customLabel!}
							label="Custom Label"
							handleChange={handleCustomLabelChange}
							width="150px"
						/>
						<CustomCheckBox
							label="Label Visibility"
							value={options.labelVisibility!}
							handleChange={handleLabelVisibilityChange}
						/>
						<ColorBox
							label="Label Color"
							value={options.labelColor!}
							handleChange={handleLabelColorChange}
						/>
					</Flex>
				</Box>

				<CustomOptions options={options} progressValue={value} />
			</Flex>
		</Box>
	);
};
